import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Icon, Modal, Switch } from "../../";
import { useBusinessToggle } from "../../../../hooks";

const BusinessCard = ({ business }) => {
  const { t } = useTranslation("navigation");
  const navigate = useNavigate();
  const toggleBusinessMutation = useBusinessToggle();
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);

  const { id, name, logo, enabled, city } = business;

  const hideStatus = true;

  const getStatusColor = () => {
    return enabled
      ? "bg-green-900/20 text-green-400 border-green-500/30"
      : "bg-red-900/20 text-red-400 border-red-500/30";
  };

  const getBorderColor = () => {
    return enabled ? "border-green-500/50" : "border-red-500/50";
  };

  const getStatusText = () => {
    return enabled ? "Active" : "Inactive";
  };

  const handleEditClick = (e) => {
    e.stopPropagation();
    navigate(`/menu/stores/${id}`);
  };

  const handleToggleBusiness = (checked) => {
    if (checked) {
      handleConfirmToggle();
    } else {
      setShowDeactivateModal(true);
    }
  };

  const handleConfirmToggle = async () => {
    try {
      const newEnabledStatus = !enabled;

      await toggleBusinessMutation.mutateAsync({
        businessId: id,
        enabled: newEnabledStatus,
      });

      setShowDeactivateModal(false);
    } catch (error) {
      console.error("Toggle business status failed:", error);
    }
  };

  return (
    <>
      <div
        className={`rounded-lg border-2 bg-gray-900 p-4 transition-colors hover:bg-gray-800 ${getBorderColor()}`}
      >
        {/* Business Info */}
        <div className="space-y-2">
          {/* Logo and Name */}
          <div className="flex items-start space-x-3">
            {logo ? (
              <img
                src={logo}
                alt={`${name} logo`}
                className="h-12 w-12 flex-shrink-0 rounded-lg object-cover"
                onError={(e) => {
                  e.target.style.display = "none";
                }}
              />
            ) : (
              <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-lg bg-gray-800">
                <div className="text-xs text-gray-400">No logo</div>
              </div>
            )}

            <div className="min-w-0 flex-1">
              <h3 className="line-clamp-2 text-sm leading-tight font-semibold text-white">
                {name}
              </h3>
              <p className="mt-1 text-xs text-gray-400">
                {city?.name || "Unknown City"}
              </p>
            </div>
          </div>

          {/* Business Status */}
          {!hideStatus && (
            <div className="flex items-center space-x-2">
              <span className="text-xs text-gray-400">Business Status:</span>
              <div
                className={`rounded-full border px-2 py-1 text-xs font-medium ${getStatusColor()}`}
              >
                {getStatusText()}
              </div>
            </div>
          )}
          {/* POS Integrations Link */}
          <div className="mt-3 border-t border-gray-700 pt-3">
            <button
              onClick={(e) => {
                e.stopPropagation();
                navigate("/integrations/pos", { state: { business } });
              }}
              className="flex w-full items-center justify-center space-x-2 rounded-md border border-blue-500/30 bg-blue-900/20 px-3 py-2 text-xs font-medium text-blue-400 transition-colors hover:bg-blue-900/30"
            >
              <Icon name="Link" className="h-3 w-3" />
              <span>View POS Systems</span>
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                navigate("/growth/link-in-bio");
              }}
              className="mt-2 flex w-full items-center justify-center space-x-2 rounded-md border border-violet-500/30 bg-violet-900/20 px-3 py-2 text-xs font-medium text-violet-300 transition-colors hover:bg-violet-900/30"
            >
              <Icon name="Link2" className="h-3 w-3" />
              <span>{t("businessCard.manageLinkInBio")}</span>
            </button>
          </div>
        </div>

        {/* Footer - Actions */}
        <div className="mt-2 border-t border-gray-700 pt-3">
          <div className="flex items-center justify-between">
            <button
              onClick={handleEditClick}
              className="rounded bg-gray-800 px-2 py-1 text-xs text-gray-300 transition-colors hover:bg-gray-700"
            >
              Edit
            </button>
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-400">
                {enabled ? "Enabled" : "Disabled"}
              </span>
              <Switch
                checked={enabled}
                onCheckedChange={handleToggleBusiness}
                disabled={toggleBusinessMutation.isPending}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Deactivate Business Confirmation Modal */}
      <Modal
        isOpen={showDeactivateModal}
        onClose={() => setShowDeactivateModal(false)}
        title="Deactivate Store"
        subtitle="Are you sure you want to deactivate this store?"
        maxWidth="max-w-md"
      >
        <div className="space-y-4">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-500/20">
                <Icon name="AlertTriangle" className="h-5 w-5 text-red-400" />
              </div>
            </div>
            <div className="flex-1">
              <h3 className="text-sm font-medium text-white">
                Deactivate {name}?
              </h3>
              <p className="mt-1 text-sm text-gray-400">
                This store will be disabled and may affect its operations. You
                can reactivate it later if needed.
              </p>
            </div>
          </div>

          <div className="flex space-x-3 pt-4">
            <button
              onClick={handleConfirmToggle}
              disabled={toggleBusinessMutation.isPending}
              className="flex flex-1 items-center justify-center space-x-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {toggleBusinessMutation.isPending ? (
                <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-white"></div>
              ) : null}
              <span>
                {toggleBusinessMutation.isPending
                  ? "Deactivating..."
                  : "Deactivate Store"}
              </span>
            </button>
            <button
              onClick={() => setShowDeactivateModal(false)}
              disabled={toggleBusinessMutation.isPending}
              className="flex-1 rounded-lg border border-gray-700 bg-gray-800 px-4 py-2 text-sm font-medium text-gray-300 transition-colors hover:bg-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default BusinessCard;
